import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Upload, Loader2, Receipt, ChevronDown, ChevronRight, Trash2, Undo2 } from "lucide-react";
import { StatementScanResults } from "@/components/StatementScanResults";
import type { ScannedStatement, ScannedTransaction } from "@shared/schema";
import { dedupeObligationsByPayee } from "@shared/obligations-dedupe";

interface StatementWithCount extends ScannedStatement {
  transactionCount: number;
  recurringCount: number;
  nonRecurringCount: number;
}

interface ObligationsGroup {
  category: string;
  items: ScannedTransaction[];
}

interface ObligationsReport {
  groups: ObligationsGroup[];
  totalMonthly: string;
  totalAnnual: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  utility: "Utilities",
  subscription: "Subscriptions",
  insurance: "Insurance",
  mortgage: "Mortgage / Loans",
  rent: "Rent",
  loan_payment: "Loan Payments",
  investment: "Investments",
  government: "Government",
  other: "Other",
};

interface ActionButton {
  label: string;
  action: string;
  tooltip: string;
}

const ACTION_BUTTONS: Record<string, ActionButton[]> = {
  utility: [
    { label: "Cancel", action: "handled", tooltip: "Mark as cancelled. You can generate a cancellation letter for this payee." },
    { label: "Transfer", action: "handled", tooltip: "Mark for transfer to surviving spouse or estate account. The payment continues under new ownership." },
    { label: "Keep paying", action: "handled", tooltip: "Mark as ongoing. The estate will continue making this payment during settlement." },
  ],
  subscription: [
    { label: "Cancel", action: "handled", tooltip: "Mark as cancelled. You can generate a cancellation letter for this payee." },
    { label: "Ignore", action: "ignored", tooltip: "Remove from obligations tracking. This is not a recurring charge." },
  ],
  insurance: [
    { label: "File claim", action: "handled", tooltip: "Mark for insurance claim. Generate a claim letter for this provider." },
    { label: "Keep paying", action: "handled", tooltip: "Mark as ongoing. The estate will continue making this payment during settlement." },
    { label: "Cancel", action: "handled", tooltip: "Mark as cancelled. You can generate a cancellation letter for this payee." },
  ],
  mortgage: [
    { label: "Contact lender", action: "handled", tooltip: "Flag for direct contact with the lender about the mortgage." },
  ],
  rent: [
    { label: "Contact landlord", action: "handled", tooltip: "Flag for direct contact with the landlord." },
    { label: "Keep paying", action: "handled", tooltip: "Mark as ongoing. The estate will continue making this payment during settlement." },
  ],
  loan_payment: [
    { label: "Contact lender", action: "handled", tooltip: "Flag for direct contact with the lender about the mortgage." },
  ],
  investment: [
    { label: "Keep paying", action: "handled", tooltip: "Mark as ongoing. The estate will continue making this payment during settlement." },
    { label: "Cancel", action: "handled", tooltip: "Mark as cancelled. You can generate a cancellation letter for this payee." },
  ],
  government: [
    { label: "Keep paying", action: "handled", tooltip: "Mark as ongoing. The estate will continue making this payment during settlement." },
    { label: "Ignore", action: "ignored", tooltip: "Remove from obligations tracking. This is not a recurring charge." },
  ],
  other: [
    { label: "Keep paying", action: "handled", tooltip: "Mark as ongoing. The estate will continue making this payment during settlement." },
    { label: "Ignore", action: "ignored", tooltip: "Remove from obligations tracking. This is not a recurring charge." },
  ],
};

const STATUS_LABELS: Record<string, string> = {
  handled: "Handled",
  ignored: "Ignored",
  added: "Added",
};

/**
 * One uploaded file's outcome. Every file gets a row whether it worked or not — a batch
 * that silently drops the file that failed is worse than no batch at all, because the
 * planner has no way to know which of the three statements did not land.
 */
interface BatchFileResult {
  fileName: string;
  status: "pending" | "scanning" | "done" | "error";
  transactionCount?: number;
  recurringCount?: number;
  message?: string;
  statementId?: string;
}

interface BatchState {
  files: BatchFileResult[];
  /** Every transaction from every file that scanned, in upload order. */
  transactions: ScannedTransaction[];
  /** The first successfully scanned statement — the results table needs one for its shape. */
  firstStatement: ScannedStatement | null;
}

interface Props {
  clientId: string;
  /**
   * Which surface this is mounted on (s217, STATEMENT-SCANNER-PLANNER-1).
   *
   * "executor" is the original, post-death surface: the results table offers the
   * cancel / transfer / keep-paying actions and there is nothing to add to a plan,
   * because the plan is finished.
   *
   * "planner" is the surface the three-months ruling asked for: the same scan, but the
   * results table offers "Add to my plan". The picker's planner branch and
   * `bulk-add` were written in s202 and have had NO LIVE CALLER since — this prop is
   * what connects them.
   *
   * Default stays "executor" so the existing mount is unchanged by this prop existing.
   */
  mode?: "planner" | "executor";
}

export default function StatementScannerPage({ clientId, mode = "executor" }: Props) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [selectedStatementData, setSelectedStatementData] = useState<{ statement: ScannedStatement; transactions: ScannedTransaction[] } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(["utility", "subscription", "insurance", "mortgage"]));
  const [handledExpanded, setHandledExpanded] = useState(false);
  // Batch upload (s217). `batch` is null for a single-file upload, which keeps the
  // original one-statement flow exactly as it was.
  const [batch, setBatch] = useState<BatchState | null>(null);
  // Read synchronously by the mutation callbacks, which must not act while a batch owns
  // the progress state. A state flag would be a render behind.
  const batchRef = useRef(false);

  const { data: statements = [], isLoading: statementsLoading } = useQuery<StatementWithCount[]>({
    queryKey: ["/api/clients", clientId, "scanned-statements"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/scanned-statements`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  const { data: report } = useQuery<ObligationsReport>({
    queryKey: ["/api/clients", clientId, "obligations-report"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/obligations-report`);
      if (!res.ok) return { groups: [], totalMonthly: "0.00", totalAnnual: "0.00" };
      return res.json();
    },
    enabled: !!clientId,
  });

  const scanFlight = useSingleFlight();

  const scanMutation = useMutation({
    mutationFn: async (file: File) => {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = "";
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);
      const res = await apiRequest("POST", `/api/clients/${clientId}/scan-statement`, {
        data: base64,
        contentType: file.type,
        filename: file.name,
      });
      return res.json() as Promise<{ statement: ScannedStatement; transactions: ScannedTransaction[] }>;
    },
    onSuccess: ({ statement, transactions }) => {
      // In a BATCH the loop owns the progress state, the toast and the results view —
      // otherwise every file would fire its own toast and steal the selected statement.
      if (batchRef.current) return;
      setScanning(false);
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "scanned-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "obligations-report"] });
      const recurringCount = transactions.filter(t => t.isRecurring).length;
      toast({
        title: "Statement uploaded",
        description: `${transactions.length} transactions found, ${recurringCount} recurring`,
      });
      setSelectedStatementId(statement.id);
      setSelectedStatementData({ statement, transactions });
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (error: any) => {
      // The batch loop catches the rejected promise per file and records the reason in
      // its own row; mutateAsync rejects regardless of what this handler does.
      if (batchRef.current) return;
      setScanning(false);
      const isServiceError = error?.status === 503 || error?.status === 500 || error?.message?.includes("503") || error?.message?.includes("500");
      toast({
        title: "Error",
        description: isServiceError
          ? "Our document scanner is temporarily busy. Please try again in a moment."
          : error.message,
        variant: "destructive",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ transactionId, status }: { transactionId: string; status: string }) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientId}/scanned-transactions/${transactionId}`, { status });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "scanned-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "obligations-report"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteStatementMutation = useMutation({
    mutationFn: async (statementId: string) => {
      const res = await apiRequest("DELETE", `/api/clients/${clientId}/scanned-statements/${statementId}`, undefined);
      return res.json();
    },
    onSuccess: (_, statementId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "scanned-statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "obligations-report"] });
      if (selectedStatementId === statementId) {
        setSelectedStatementId(null);
        setSelectedStatementData(null);
      }
      toast({ title: "Statement removed" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const ALLOWED_TYPES = [
    "image/jpeg", "image/png", "image/gif", "image/webp",
    "application/pdf",
    "text/csv", "text/tab-separated-values",
    "application/csv", "application/vnd.ms-excel",
    "application/x-ofx", "application/vnd.intu.qfx",
  ];
  const isAllowedFile = (file: File) =>
    ALLOWED_TYPES.includes(file.type) || /\.(csv|tsv|ofx|qfx)$/i.test(file.name);

  /**
   * BATCH MODE (s217). The optional "check your bills" step asks for THREE MONTHS of
   * statements per account, so uploading them one at a time was the whole friction.
   *
   * 🔴 Sequential on purpose, through the EXISTING single-statement POST. The job/worker
   * queue would be the right home for true parallel batch and Lane B2 sized that
   * SMALL–MEDIUM; it is explicitly out of this lane. Sequential also keeps us honest with
   * Gemini's rate limits and makes the per-file result row trivially correct.
   */
  const handleFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;

    const rejected = files.filter(f => !isAllowedFile(f));
    const accepted = files.filter(isAllowedFile);
    if (rejected.length > 0) {
      toast({
        title: rejected.length === files.length ? "Invalid file" : "Some files skipped",
        description: `${rejected.map(f => f.name).join(", ")} — please upload PDF, image, CSV, or OFX files.`,
        variant: "destructive",
      });
    }
    if (accepted.length === 0) return;

    setSelectedStatementId(null);
    setSelectedStatementData(null);

    if (accepted.length === 1) {
      // One file: the original flow, untouched.
      setBatch(null);
      batchRef.current = false;
      setScanning(true);
      // SINGLE-FLIGHT-PRIMITIVE-1 (s170): fires from a FILE-DROP handler, not a button — there is no
      // control to disable, which is exactly why the primitive wraps the action. Each scan is a
      // billable Gemini call plus a persisted statement row.
      scanFlight.runQuiet(() => scanMutation.mutateAsync(accepted[0]));
      return;
    }

    setBatch({
      files: accepted.map(f => ({ fileName: f.name, status: "pending" as const })),
      transactions: [],
      firstStatement: null,
    });
    setScanning(true);
    batchRef.current = true;
    // ONE flight for the WHOLE batch — a second drop mid-batch would otherwise interleave
    // two sequences of billable calls into the same result state.
    scanFlight.runQuiet(() => runBatch(accepted).finally(() => { batchRef.current = false; }));
  };

  const runBatch = async (files: File[]) => {
    for (let i = 0; i < files.length; i++) {
      setBatch(prev => prev && {
        ...prev,
        files: prev.files.map((f, idx) => (idx === i ? { ...f, status: "scanning" } : f)),
      });
      try {
        const { statement, transactions } = await scanMutation.mutateAsync(files[i]);
        setBatch(prev => prev && {
          files: prev.files.map((f, idx) => (idx === i ? {
            ...f,
            status: "done",
            transactionCount: transactions.length,
            recurringCount: transactions.filter(t => t.isRecurring).length,
            statementId: statement.id,
          } : f)),
          transactions: [...prev.transactions, ...transactions],
          firstStatement: prev.firstStatement ?? statement,
        });
      } catch (error: any) {
        // One bad statement does not abandon the rest — the row records why and the loop
        // continues. This is the reason the batch is sequential and not Promise.all.
        setBatch(prev => prev && {
          ...prev,
          files: prev.files.map((f, idx) => (idx === i ? {
            ...f,
            status: "error",
            message: error?.status === 422
              ? "Not a bank or credit card statement"
              : (error?.message ?? "Upload failed"),
          } : f)),
        });
      }
    }
    setScanning(false);
    if (fileRef.current) fileRef.current.value = "";
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "scanned-statements"] });
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "obligations-report"] });
  };

  const toggleGroup = (cat: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const toMonthly = (amount: string, freq: string | null): number => {
    const n = parseFloat(amount) || 0;
    if (freq === "biweekly") return n * 2.17;
    if (freq === "quarterly") return n / 3;
    if (freq === "annual") return n / 12;
    return n;
  };

  // G7: the dedupe rule is the obligations report's, lifted to shared/ in s217 rather than
  // re-typed here. A duplicate across three months is one obligation on BOTH surfaces.
  const combinedTransactions = batch
    ? dedupeObligationsByPayee(batch.transactions)
    : [];

  const allItems = report?.groups.flatMap(g => g.items) ?? [];
  const pendingItems = allItems.filter(t => t.status === "pending");
  const handledItems = allItems.filter(t => t.status !== "pending");

  return (
    <TooltipProvider>
      <div className="container max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Statement Scanner</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Upload bank and credit card statements (PDF, image, CSV, or OFX) to discover recurring obligations
          </p>
        </div>

        {/* Upload section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Upload a statement</CardTitle>
          </CardHeader>
          <CardContent>
            {scanning ? (
              <div className="flex items-center gap-3 py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <p className="text-sm">
                  {batch
                    ? `Analyzing statement ${Math.min(batch.files.filter(f => f.status !== "pending").length, batch.files.length)} of ${batch.files.length}...`
                    : "Analyzing statement..."}
                </p>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <Button onClick={() => fileRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload PDFs, CSVs, or images
                </Button>
                <p className="text-xs text-muted-foreground sm:self-center">
                  You can select several at once — three months per account is ideal.
                </p>
                {/* s217: `multiple` is the batch affordance. Everything else about this
                    input is unchanged; the handler decides single vs batch. */}
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.csv,.tsv,.ofx,.qfx,text/csv,text/tab-separated-values"
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Batch results: one row per FILE, then ONE combined results view ─────────
            Per-file rows are not decoration. A batch that reports only a total leaves the
            planner unable to tell which of three statements failed and needs re-uploading. */}
        {batch && (
          <Card data-testid="batch-results">
            <CardHeader>
              <CardTitle className="text-base">
                {batch.files.length} statements uploaded
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {batch.files.map((f, i) => (
                  <div key={`${f.fileName}-${i}`} className="flex items-center justify-between px-4 py-2.5 gap-3" data-testid="batch-file-row">
                    <p className="text-sm truncate flex-1 min-w-0">{f.fileName}</p>
                    <p className={`text-xs shrink-0 ${f.status === "error" ? "text-destructive" : "text-muted-foreground"}`}>
                      {f.status === "pending" && "Waiting"}
                      {f.status === "scanning" && "Analyzing..."}
                      {f.status === "done" && `${f.transactionCount} transactions, ${f.recurringCount} recurring`}
                      {f.status === "error" && (f.message ?? "Failed")}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* The combined view. Cross-statement duplicates are collapsed by
            `dedupeObligationsByPayee` — the SAME function the obligations report uses
            server-side (shared/obligations-dedupe.ts), so the two never disagree about
            how many bills the planner has. */}
        {batch && !scanning && batch.firstStatement && combinedTransactions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Scan Results</CardTitle>
            </CardHeader>
            <CardContent>
              <StatementScanResults
                clientId={clientId}
                statement={batch.firstStatement}
                transactions={combinedTransactions}
                showAllDefault={true}
                mode={mode}
                heading={`${batch.files.filter(f => f.status === "done").length} statements combined`}
                onDone={() => setBatch(null)}
              />
            </CardContent>
          </Card>
        )}

        {/* Statement management list */}
        {statements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your Statements</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {statements.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-4 py-3 gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {s.institutionName ?? "Statement"}
                        {s.accountLast4 && <span className="text-muted-foreground font-normal ml-1">···{s.accountLast4}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {s.statementPeriodStart && s.statementPeriodEnd
                          ? `${s.statementPeriodStart} – ${s.statementPeriodEnd} · `
                          : ""}
                        {s.recurringCount} recurring, {s.nonRecurringCount} other
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        className="text-xs text-primary underline underline-offset-2"
                        onClick={() => {
                          if (selectedStatementId === s.id) {
                            setSelectedStatementId(null);
                            setSelectedStatementData(null);
                          } else {
                            setSelectedStatementId(s.id);
                            fetch(`/api/clients/${clientId}/scanned-statements/${s.id}`)
                              .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
                              .then(data => setSelectedStatementData(data))
                              .catch(() => toast({ title: "Error", description: "Could not load statement results.", variant: "destructive" }));
                          }
                        }}
                      >
                        {selectedStatementId === s.id ? "Hide results" : "View results"}
                      </button>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Delete statement"
                        onClick={() => deleteStatementMutation.mutate(s.id)}
                        disabled={deleteStatementMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Inline scan results for selected statement */}
        {selectedStatementData && selectedStatementId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Scan Results</CardTitle>
            </CardHeader>
            <CardContent>
              <StatementScanResults
                clientId={clientId}
                statement={selectedStatementData.statement}
                transactions={selectedStatementData.transactions}
                showAllDefault={true}
                mode={mode}
                onDone={() => { setSelectedStatementId(null); setSelectedStatementData(null); }}
              />
            </CardContent>
          </Card>
        )}

        {/* Obligations report */}
        {report && report.groups.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Recurring Obligations</h2>
                {statements.length > 1 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Obligations combined from {statements.length} uploaded statements. Duplicate charges across statements are shown once.
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold">${parseFloat(report.totalMonthly).toLocaleString()}/month</p>
                <p className="text-xs text-muted-foreground">${parseFloat(report.totalAnnual).toLocaleString()}/year</p>
              </div>
            </div>

            {/* Requiring action */}
            {pendingItems.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Requiring action</h3>
                {report.groups.map(group => {
                  const groupPendingItems = group.items.filter(t => t.status === "pending");
                  if (groupPendingItems.length === 0) return null;
                  const groupMonthly = groupPendingItems.reduce((sum, t) => sum + toMonthly(t.amount, t.frequency), 0);
                  const expanded = expandedGroups.has(group.category);
                  return (
                    <Card key={group.category}>
                      <button
                        className="w-full flex items-center justify-between p-4 text-left"
                        onClick={() => toggleGroup(group.category)}
                      >
                        <div className="flex items-center gap-2">
                          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="font-medium">{CATEGORY_LABELS[group.category] ?? group.category}</span>
                          <Badge variant="outline" className="text-xs">{groupPendingItems.length} item{groupPendingItems.length !== 1 ? "s" : ""}</Badge>
                        </div>
                        <span className="text-sm font-semibold text-muted-foreground">${groupMonthly.toFixed(0)}/mo</span>
                      </button>
                      {expanded && (
                        <CardContent className="pt-0 pb-4">
                          <div className="space-y-2">
                            {groupPendingItems.map(t => (
                              <div key={t.id} className="flex items-center justify-between py-2 border-t">
                                <div className="flex-1 min-w-0 mr-3">
                                  <p className="text-sm font-medium truncate">{t.cleanName}</p>
                                  <p className="text-xs text-muted-foreground">${parseFloat(t.amount).toFixed(2)}/{t.frequency ?? "month"}</p>
                                </div>
                                <div className="flex gap-1 flex-wrap justify-end">
                                  {(ACTION_BUTTONS[t.category] ?? ACTION_BUTTONS.other).map(btn => (
                                    <Tooltip key={btn.label}>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="text-xs h-7 px-2"
                                          onClick={() => updateStatusMutation.mutate({ transactionId: t.id, status: btn.action })}
                                          disabled={updateStatusMutation.isPending}
                                        >
                                          {btn.label}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[220px] text-xs">
                                        {btn.tooltip}
                                      </TooltipContent>
                                    </Tooltip>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Handled section */}
            {handledItems.length > 0 && (
              <div className="space-y-2">
                <button
                  className="flex items-center gap-2 text-sm font-medium text-muted-foreground uppercase tracking-wide w-full text-left"
                  onClick={() => setHandledExpanded(prev => !prev)}
                >
                  {handledExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Handled ({handledItems.length})
                </button>
                {handledExpanded && (
                  <Card>
                    <CardContent className="p-0">
                      <div className="divide-y">
                        {handledItems.map(t => (
                          <div key={t.id} className="flex items-center justify-between px-4 py-3 gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{t.cleanName}</p>
                              <p className="text-xs text-muted-foreground">${parseFloat(t.amount).toFixed(2)}/{t.frequency ?? "month"}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge variant="secondary" className="text-xs capitalize">
                                {STATUS_LABELS[t.status] ?? t.status}
                              </Badge>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2 text-xs text-muted-foreground"
                                    onClick={() => updateStatusMutation.mutate({ transactionId: t.id, status: "pending" })}
                                    disabled={updateStatusMutation.isPending}
                                  >
                                    <Undo2 className="w-3 h-3 mr-1" />
                                    Undo
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  Move back to Requiring action
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Summary */}
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-lg font-bold">${parseFloat(report.totalMonthly).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total monthly</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">${parseFloat(report.totalAnnual).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total annual</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{pendingItems.length}</p>
                    <p className="text-xs text-muted-foreground">Requiring action</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{handledItems.length}</p>
                    <p className="text-xs text-muted-foreground">Handled</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {!statementsLoading && statements.length === 0 && !scanning && (
          <Card>
            <CardContent className="py-10 text-center">
              <Receipt className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Upload a bank or credit card statement (PDF, image, CSV, or OFX) to get started</p>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
